import React, { useCallback, useEffect, useState } from "react";

import { fetchSaccos } from "../services/apiCalls";

export type SaccoOption = {
  id: string;
  label: string;
};

/**
 * Normalizes GET /sacco responses for select options (array, { content }, { data }, or mixed item shapes).
 */
export function normalizeSaccosResponse(raw: unknown): SaccoOption[] {
  let list: unknown[] = [];
  if (Array.isArray(raw)) {
    list = raw;
  } else if (raw && typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    if (Array.isArray(o.content)) list = o.content;
    else if (Array.isArray(o.data)) list = o.data;
    else if (Array.isArray(o.saccos)) list = o.saccos;
  }
  return list
    .map((item): SaccoOption | null => {
      if (item == null) return null;
      if (typeof item === "string" || typeof item === "number") {
        const id = String(item);
        return { id, label: id };
      }
      if (typeof item !== "object") return null;
      const row = item as Record<string, unknown>;
      const id = row.id ?? row.saccoId ?? row.sacco ?? row.code;
      if (id == null) return null;
      const label =
        row.saccoName ??
        row.name ??
        row.label ??
        row.title ??
        row.displayName ??
        id;
      return { id: String(id), label: String(label) };
    })
    .filter((x): x is SaccoOption => x !== null && x.id.length > 0);
}

/** Stable string id from a member’s `sacco` field (id, sacco id, or nested `{ id }`). */
export function memberSaccoIdKey(member: Record<string, unknown>): string {
  const raw = member.sacco;
  if (raw == null || raw === "") return "";
  if (typeof raw === "object" && raw !== null && "id" in raw) {
    const v = (raw as { id: unknown }).id;
    return v != null ? String(v) : "";
  }
  return String(raw).trim();
}

/** Map from GET /sacco–normalized options for table display. */
export function saccoOptionsToLookupMap(options: SaccoOption[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const o of options) {
    m.set(o.id, o.label);
  }
  return m;
}

/**
 * Prefer sacco name from GET /sacco (lookup by member’s sacco id); otherwise show raw `sacco` value.
 */
export function resolveMemberSaccoDisplayName(
  member: Record<string, unknown>,
  lookup: Map<string, string>
): string {
  const key = memberSaccoIdKey(member);
  if (key) {
    const name = lookup.get(key);
    if (name != null && String(name).trim() !== "") return name;
  }
  return key || "—";
}

export type SaccoSelectProps = {
  value: string;
  onChange: (saccoId: string) => void;
  label?: string;
  id?: string;
  name?: string;
  className?: string;
  selectClassName?: string;
  disabled?: boolean;
  placeholder?: string;
  /** When set, skips internal fetch and uses these options (e.g. tests or parent-loaded data). */
  options?: SaccoOption[];
  /** Include an empty first option (value ""). */
  allowEmpty?: boolean;
  emptyLabel?: string;
};

const SaccoSelect = ({
  value,
  onChange,
  label = "Select Sacco",
  id = "sacco-select",
  name = "saccoId",
  className = "",
  selectClassName = "form-control",
  disabled = false,
  placeholder = "Select sacco",
  options: optionsProp,
  allowEmpty = false,
  emptyLabel = "—",
}: SaccoSelectProps) => {
  const [options, setOptions] = useState<SaccoOption[]>(optionsProp ?? []);
  const [loading, setLoading] = useState(!optionsProp);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (optionsProp) {
      setOptions(optionsProp);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const raw = await fetchSaccos();
      setOptions(normalizeSaccosResponse(raw));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load saccos.");
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [optionsProp]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className={`form-group mb-0 ${className}`.trim()}>
      {label ? (
        <label htmlFor={id} className="mb-1">
          {label}
        </label>
      ) : null}
      <select
        id={id}
        name={name}
        className={selectClassName}
        value={value}
        disabled={disabled || loading}
        onChange={(e) => onChange(e.target.value)}
        aria-busy={loading}
        aria-invalid={error ? true : undefined}
      >
        {allowEmpty ? (
          <option value="">{emptyLabel}</option>
        ) : (
          <option value="" disabled>
            {loading ? "Loading…" : placeholder}
          </option>
        )}
        {value &&
          !options.some((o) => o.id === value) ? (
          <option value={value}>{value}</option>
        ) : null}
        {options.map((opt) => (
          <option key={opt.id} value={opt.id}>
            {opt.label}
          </option>
        ))}
      </select>
      {error ? <div className="text-danger small mt-1">{error}</div> : null}
    </div>
  );
};

export default SaccoSelect;
